// Cross-subdomain UTM attribution. Reads the `os_utm` cookie set by Webflow GTM
// on `.optisigns.com` (only written on real UTM arrivals, so `l` branch is the
// most recent campaign-tagged arrival, not a stale www visit).

import posthog from 'posthog-js';
import { safeJsonParse } from './safe-format';

type UtmBranch = {
  s?: string;
  m?: string;
  c?: string;
  ct?: string;
  t?: string;
  r?: string;
};
type OsUtmCookie = { i?: UtmBranch; l?: UtmBranch };

function clean(obj: Record<string, string | undefined>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v) out[k] = v;
  }
  return out;
}

function readOsUtmCookie(): OsUtmCookie | null {
  const match = document.cookie.match(/(?:^|; )os_utm=([^;]*)/);
  if (!match) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(match[1]);
  } catch {
    return null;
  }
  return safeJsonParse<OsUtmCookie | null>(decoded, null);
}

function parseUrlUtms(url: string): Record<string, string> {
  try {
    const u = new URL(url, window.location.href);
    return clean({
      latest_utm_source: u.searchParams.get('utm_source') ?? undefined,
      latest_utm_medium: u.searchParams.get('utm_medium') ?? undefined,
      latest_utm_campaign: u.searchParams.get('utm_campaign') ?? undefined,
      latest_utm_content: u.searchParams.get('utm_content') ?? undefined,
      latest_utm_term: u.searchParams.get('utm_term') ?? undefined,
    });
  } catch {
    return {};
  }
}

function getExternalReferringDomain(): string {
  if (!document.referrer) return '';
  try {
    const refHost = new URL(document.referrer).hostname;
    if (refHost === window.location.hostname) return '';
    return refHost;
  } catch {
    return '';
  }
}

function readCookieLatestUtms(): Record<string, string> {
  const cookie = readOsUtmCookie();
  if (!cookie?.l) return {};
  return clean({
    latest_utm_source: cookie.l.s,
    latest_utm_medium: cookie.l.m,
    latest_utm_campaign: cookie.l.c,
    latest_utm_content: cookie.l.ct,
    latest_utm_term: cookie.l.t,
    latest_referring_domain: cookie.l.r,
  });
}

function readCookieInitialUtms(): Record<string, string> {
  const cookie = readOsUtmCookie();
  if (!cookie?.i) return {};
  return clean({
    initial_utm_source: cookie.i.s,
    initial_utm_medium: cookie.i.m,
    initial_utm_campaign: cookie.i.c,
    initial_utm_content: cookie.i.ct,
    initial_utm_term: cookie.i.t,
    initial_referring_domain: cookie.i.r,
  });
}

function getPosthogProperty(key: string): string | undefined {
  try {
    const v = posthog.get_property?.(key);
    return typeof v === 'string' ? v : undefined;
  } catch {
    return undefined;
  }
}

// Cookie's `i` isn't truly "initial" (Webflow overwrites it each UTM visit);
// PostHog's $set_once property is the true first-touch.
export function getInitialUtms(): Record<string, string> {
  const fromCookie = readCookieInitialUtms();
  const keys = [
    'initial_utm_source',
    'initial_utm_medium',
    'initial_utm_campaign',
    'initial_utm_content',
    'initial_utm_term',
    'initial_referring_domain',
  ];
  const out: Record<string, string> = {};
  for (const key of keys) {
    const value = getPosthogProperty(key) ?? fromCookie[key];
    if (value) out[key] = value;
  }
  return out;
}

// Resolution order: current URL → document.referrer → `os_utm.l` cookie.
export function getSessionLatestUtms(): Record<string, string> {
  const fromUrl = parseUrlUtms(window.location.href);
  if (fromUrl.latest_utm_source) {
    const refDomain = getExternalReferringDomain();
    if (refDomain) fromUrl.latest_referring_domain = refDomain;
    return fromUrl;
  }

  if (document.referrer) {
    const fromReferrer = parseUrlUtms(document.referrer);
    if (fromReferrer.latest_utm_source) {
      const refDomain = getExternalReferringDomain();
      if (refDomain) fromReferrer.latest_referring_domain = refDomain;
      return fromReferrer;
    }
  }

  return readCookieLatestUtms();
}
